import {
    addMinutes,
    format,
    getYear,
    isValid,
    parseISO,
    differenceInSeconds,
    differenceInYears,
    isBefore,
    subYears,
    parse,
} from 'date-fns';

export const formatTimestamp = (date, pattern = 'yyyy-MM-dd HH:mm:ss') => format(date, pattern);

export const minutesFromNow = (minutes) => addMinutes(new Date(), minutes);

// `isValid` only accepts a Date or number and returns false for any string, so coerce
// string inputs into a Date first. Smart capture SDK sends birth dates as Microsoft/ASP.NET JSON
// dates, e.g. "/Date(539049600000)/" or "/Date(539049600000-0700)/", where the leading
// number is epoch milliseconds and the optional trailing offset is informational only.
const coerceToDate = (value) => {
    if (typeof value !== 'string') return value;
    if (value.startsWith('/Date(')) {
        // parseInt stops at the first non-digit, so it ignores the ")/" and any offset.
        return new Date(Number.parseInt(value.slice('/Date('.length), 10));
    }
    return parseISO(value);
};

export const isValidDate = (value) => isValid(coerceToDate(value));

// Whole seconds remaining until `target` (negative once past). Truncates toward zero.
export const secondsUntil = (target) => differenceInSeconds(new Date(target), new Date());

// current 4-digit year for the copyright line. Replaces `moment().year()`.
export const getCurrentYear = () => getYear(new Date());

// replaces `moment(x).format('DD-MM-YYYY')` in the education mapper.
export const formatDayMonthYear = (date) => format(date, 'dd-MM-yyyy');

// Date of birth as stored (yyyy-MM-dd) → form display (MM-dd-yyyy). Null-safe.
export const toDisplayDob = (value) => {
    if (!value) return null;
    const date = parse(value, 'yyyy-MM-dd', new Date());
    return isValid(date) ? format(date, 'MM-dd-yyyy') : null;
};

// Form display dob (MM-dd-yyyy) → API payload format (dd-MM-yyyy, = DEFAULT_DATE_FORMAT). Null-safe.
export const toApiDob = (value) => {
    if (!value) return null;
    const date = parse(value, 'MM-dd-yyyy', new Date());
    return isValid(date) ? format(date, 'dd-MM-yyyy') : null;
};

// True when `value` (a yyyy-MM-dd string, or null/invalid) is earlier than now.
// Invalid or missing input yields false. Used for document-expiry checks.
export const isDateInPast = (value) => {
    const date = coerceToDate(value);
    return isValid(date) && isBefore(date, new Date());
};

// Whole years elapsed since a birth date given as MM-dd-yyyy. Null when the date cannot be parsed.
export const getDobAgeYears = (value) => {
    const date = parse(value, 'MM-dd-yyyy', new Date());
    return isValid(date) ? differenceInYears(new Date(), date) : null;
};

// The date exactly 18 years before today, formatted MM-dd-yyyy (max selectable DOB).
export const dateEighteenYearsAgo = () => format(subYears(new Date(), 18), 'MM-dd-yyyy');
